/**
 * useSurvey Hook
 *
 * Drives the self-hosted in-app micro-survey (thumbs up/down + optional
 * message) shown by `SurveyFloatingCard`. Remote-configured via the `surveys`
 * table — change copy / enable / disable a survey with no frontend redeploy.
 *
 * Eligibility model:
 *   1. "Already answered?" -> authoritative server check on `survey_responses`
 *      (cross-device reliable), cached locally once known. Answer = stop forever.
 *   2. Impression allowance -> show up to MAX_IMPRESSIONS times (counted across
 *      navigation + refresh in AsyncStorage `survey:shownCount:{key}`). On the
 *      cap, auto-snooze for `frequency_days` (`survey:snoozeUntil:{key}`); when
 *      the snooze expires the count resets and the cycle repeats until answered.
 *   3. Dismiss (×) -> immediate snooze for `frequency_days`, bypassing the count.
 *
 * @param {('home'|'listing_detail'|null)} surface
 * @param {{ context?: object }} [opts] context is merged into the stored
 *   response + PostHog event (e.g. { listing_id }).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import supabase from '../utils/supabaseClient.js';
import logger from '../utils/logger.js';

const ANSWERED_PREFIX = 'survey:answered:';
const SHOWN_COUNT_PREFIX = 'survey:shownCount:';
const SNOOZE_UNTIL_PREFIX = 'survey:snoozeUntil:';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
// Ignore path: show this many times (across navigation + refresh) before snoozing.
const MAX_IMPRESSIONS = 3;
const UNIQUE_VIOLATION = '23505';

// A survey instance is scoped per-listing on the listing detail surface (so the
// listing survey is answerable once PER LISTING), and global per survey_key
// elsewhere. All eligibility/dedup state — local flags, throttle, session — keys
// off this so each listing is independent.
const makeInstanceKey = (surveyKey, scope) =>
  scope ? `${surveyKey}::${scope}` : surveyKey;

// Dev-only: `?survey-preview=1` forces the active survey to show, bypassing the
// throttle + answered checks (for layout/testing). Mirrors useExperiment's
// `?force-experiment-...` override. No effect in production builds.
const isPreviewMode = () => {
  try {
    return (
      typeof __DEV__ !== 'undefined' &&
      __DEV__ &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).has('survey-preview')
    );
  } catch {
    return false;
  }
};

export const useSurvey = (surface, { context } = {}) => {
  const { user } = useAuth();
  const posthog = usePostHog();

  // Listing detail scopes everything by listing_id; other surfaces are global.
  const scope = surface === 'listing_detail' ? context?.listing_id || '' : '';

  const [survey, setSurvey] = useState(null); // active config row for this surface
  const [eligible, setEligible] = useState(false); // passed throttle + not yet answered
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  // ---- Resolve active survey + eligibility on mount / surface change ----
  useEffect(() => {
    if (!surface) {
      setLoading(false);
      setEligible(false);
      setSurvey(null);
      return undefined;
    }
    // No user yet (initial load) or a transient auth flicker: bail WITHOUT
    // blanking any survey already on screen, so the card doesn't vanish when
    // `user` momentarily drops. We simply re-resolve once user?.id is back.
    if (!user?.id) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setEligible(false);
    setSurvey(null);
    setSubmitted(false);

    (async () => {
      try {
        // 1. Active surveys for this surface, highest priority first. The
        //    optional scheduling window (starts_at/ends_at) is applied
        //    client-side to avoid PostgREST .or() timestamp-parsing edge cases.
        const { data: rows, error } = await supabase
          .from('surveys')
          .select(
            'id, survey_key, surface, question, message_placeholder, frequency_days, priority, starts_at, ends_at, type, config, allow_message'
          )
          .eq('surface', surface)
          .eq('is_active', true)
          .order('priority', { ascending: false });
        if (error) throw error;
        const now = Date.now();
        const active =
          (rows || []).find((r) => {
            const startsOk = !r.starts_at || new Date(r.starts_at).getTime() <= now;
            const endsOk = !r.ends_at || new Date(r.ends_at).getTime() >= now;
            return startsOk && endsOk;
          }) || null;
        if (cancelled) return;
        if (!active) {
          setLoading(false);
          return;
        }

        // Dev preview override: always show the active survey.
        if (isPreviewMode()) {
          setSurvey(active);
          setEligible(true);
          setLoading(false);
          return;
        }

        // Instance key scopes all dedup/throttle state (per-listing on detail).
        const inst = makeInstanceKey(active.survey_key, scope);

        // 2. Answered? Local fast-path, then authoritative server count. On the
        //    listing surface the count is scoped to this listing_id, so the
        //    survey is answerable once per listing.
        const answeredKey = `${ANSWERED_PREFIX}${inst}`;
        const answeredLocal = await AsyncStorage.getItem(answeredKey);
        if (cancelled) return;
        if (answeredLocal) {
          setSurvey(active);
          setEligible(false);
          setLoading(false);
          return;
        }

        let respQuery = supabase
          .from('survey_responses')
          .select('id', { count: 'exact', head: true })
          .eq('survey_id', active.id)
          .eq('user_id', user.id);
        if (scope) respQuery = respQuery.eq('listing_id', scope);
        const { count, error: respErr } = await respQuery;
        if (respErr) throw respErr;
        if (cancelled) return;
        if (count && count > 0) {
          await AsyncStorage.setItem(answeredKey, '1');
          if (cancelled) return;
          setSurvey(active);
          setEligible(false);
          setLoading(false);
          return;
        }

        // 3. Impression allowance: eligible until either snoozed (dismiss, or
        //    the auto-snooze after MAX_IMPRESSIONS) or the snooze window is
        //    still open. markShown() increments the count + arms the snooze.
        // Clamp >= 1 day: frequency_days=0 (allowed by the CHECK) would make the
        // snooze window 0 and defeat the impression cap (shows forever).
        const freqMs = Math.max(1, active.frequency_days ?? 3) * MS_PER_DAY;
        const snoozeKey = `${SNOOZE_UNTIL_PREFIX}${inst}`;
        const countKey = `${SHOWN_COUNT_PREFIX}${inst}`;
        const [snoozeRaw, countRaw] = await Promise.all([
          AsyncStorage.getItem(snoozeKey),
          AsyncStorage.getItem(countKey),
        ]);
        if (cancelled) return;
        const nowMs = Date.now();
        let snoozeUntil = Number(snoozeRaw) || 0;
        const shownCount = Number(countRaw) || 0;

        // Snooze expired -> start a fresh impression cycle (reset the count).
        if (snoozeUntil && nowMs >= snoozeUntil) {
          snoozeUntil = 0;
          await AsyncStorage.multiRemove([snoozeKey, countKey]);
          if (cancelled) return;
        } else if (!snoozeUntil && shownCount >= MAX_IMPRESSIONS) {
          // Hit the cap but no snooze recorded yet -> begin snoozing now.
          snoozeUntil = nowMs + freqMs;
          await AsyncStorage.setItem(snoozeKey, String(snoozeUntil));
          if (cancelled) return;
        }

        const snoozing = snoozeUntil > 0 && nowMs < snoozeUntil;
        setSurvey(active);
        setEligible(!snoozing);
        setLoading(false);
      } catch (err) {
        logger.warn('[useSurvey] resolve failed:', err);
        if (!cancelled) {
          setLoading(false);
          setEligible(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [surface, user?.id, scope]);

  // ---- Record impression once the card/bubble actually shows ----
  const markShown = useCallback(async () => {
    if (!survey) return;
    const inst = makeInstanceKey(survey.survey_key, scope);
    const countKey = `${SHOWN_COUNT_PREFIX}${inst}`;
    try {
      const next = (Number(await AsyncStorage.getItem(countKey)) || 0) + 1;
      await AsyncStorage.setItem(countKey, String(next));
      // Reached the impression cap -> auto-snooze for frequency_days.
      if (next >= MAX_IMPRESSIONS) {
        const freqMs = Math.max(1, survey.frequency_days ?? 3) * MS_PER_DAY;
        await AsyncStorage.setItem(
          `${SNOOZE_UNTIL_PREFIX}${inst}`,
          String(Date.now() + freqMs)
        );
      }
    } catch (err) {
      logger.warn('[useSurvey] markShown persist failed:', err);
    }
    try {
      posthog?.capture?.('survey_shown', {
        survey_key: survey.survey_key,
        surface,
        ...(scope ? { listing_id: scope } : {}),
      });
    } catch {
      /* analytics best-effort */
    }
  }, [survey, surface, scope, posthog]);

  // ---- Submit a response ----
  const submit = useCallback(
    async ({ value, message }) => {
      if (!survey || !user?.id) return { ok: false };
      const trimmed = message?.trim() ? message.trim() : null;
      const payload = {
        survey_id: survey.id,
        survey_key: survey.survey_key,
        surface,
        question: survey.question, // snapshot the question text as shown
        answer: { value: value ?? null }, // sentiment is generated from this
        message: trimmed,
        context: context || {},
        // user_id is filled server-side by the column DEFAULT auth.uid().
      };
      try {
        const { error } = await supabase.from('survey_responses').insert(payload);
        // A unique violation means this user already answered (e.g. on another
        // device) — treat as success and stop prompting.
        if (error && error.code !== UNIQUE_VIOLATION) throw error;
        const inst = makeInstanceKey(survey.survey_key, scope);
        await AsyncStorage.setItem(`${ANSWERED_PREFIX}${inst}`, '1');
        setSubmitted(true);
        setEligible(false);
        try {
          posthog?.capture?.('survey_response', {
            survey_key: survey.survey_key,
            surface,
            question_type: survey.type,
            value,
            has_message: !!trimmed,
            ...(context || {}),
          });
        } catch {
          /* analytics best-effort */
        }
        return { ok: true };
      } catch (err) {
        logger.warn('[useSurvey] submit failed:', err);
        return { ok: false, error: err };
      }
    },
    [survey, user?.id, surface, scope, context, posthog]
  );

  // ---- Dismiss (×): immediate snooze for frequency_days (skips the count) ----
  const dismiss = useCallback(async () => {
    setEligible(false);
    if (!survey) return;
    const inst = makeInstanceKey(survey.survey_key, scope);
    try {
      const freqMs = (survey.frequency_days ?? 3) * MS_PER_DAY;
      await AsyncStorage.setItem(
        `${SNOOZE_UNTIL_PREFIX}${inst}`,
        String(Date.now() + freqMs)
      );
    } catch (err) {
      logger.warn('[useSurvey] dismiss persist failed:', err);
    }
    try {
      posthog?.capture?.('survey_dismissed', {
        survey_key: survey.survey_key,
        surface,
        ...(scope ? { listing_id: scope } : {}),
      });
    } catch {
      /* analytics best-effort */
    }
  }, [survey, surface, scope, posthog]);

  return { survey, eligible, loading, submitted, submit, dismiss, markShown };
};

export default useSurvey;
